'use client';

import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  baseAlpha: number;
  phase: number;
  colorIdx: 0 | 1 | 2;
  /** Fallback dash rotation when the cursor is off the hero. */
  rotation: number;
  /**
   * Light-mode swarm: each particle has a fixed orbital position around
   * the cursor (set by phyllotaxis at init for even spacing) plus a
   * personal "mobility" that scales how strongly it responds to the
   * cursor. Some particles are sluggish, others nimble — during a long
   * cursor traverse the swarm spreads out and trails organically instead
   * of moving as a rigid block.
   */
  orbitR: number;     // distance from cursor at rest (px)
  orbitPhase: number; // static angle around cursor (0..2π)
  orbitSpeed: number; // retained for future use; ignored when not rotating
  mobility: number;   // 0.5 .. 1.5 — multiplier on spring K and max speed
}

// (The light-mode swarm now uses dynamic HSL colour driven by particle
//  position + a slow time-based hue rotation — see the LIGHT MODE render
//  block below. The old fixed-palette constant was removed.)

// Pre-render a soft glowing dot to an offscreen canvas. Drawing this sprite
// per particle is ~10x faster than using ctx.shadowBlur in the hot loop.
function makeSprite(hsl: string, size = 24): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const g = c.getContext('2d')!;
  const cx = size / 2;
  const grad = g.createRadialGradient(cx, cx, 0, cx, cx, cx);
  grad.addColorStop(0, `hsla(${hsl} / 1)`);
  grad.addColorStop(0.25, `hsla(${hsl} / 0.55)`);
  grad.addColorStop(0.6, `hsla(${hsl} / 0.12)`);
  grad.addColorStop(1, `hsla(${hsl} / 0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  return c;
}

interface HeroParticlesProps {
  /**
   * 'dark' (default) — additive blending ('lighter'), bright glows that pop
   * against a dark background.
   * 'light' — regular alpha blending ('source-over') so particles render as
   * visible colored dots against a light background (additive blending on a
   * light bg washes everything to white and the particles disappear).
   */
  theme?: 'dark' | 'light';
}

export default function HeroParticles({ theme = 'dark' }: HeroParticlesProps = {}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Capture in a ref so the effect can react to theme changes without
  // tearing down + rebuilding the entire animation loop.
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    let width = 0;
    let height = 0;
    let dpr = 1;
    const particles: Particle[] = [];
    // Dark mode: existing density — 55 glow sprites blend nicely with additive
    // blending. Light mode: a much larger swarm (200+) clustered around the
    // cursor; small dots so the cloud reads as "alive dust", not "scattered
    // markers". The particles array is sized to the larger count; the render
    // loop iterates only the first DARK_COUNT entries in dark mode.
    const DARK_COUNT = prefersReducedMotion ? 25 : 55;
    // Light swarm density — slightly denser than the previous pass, but
    // still calmer than the original cluster. The phyllotaxis distribution
    // (see init()) keeps spacing visually even at any count.
    const LIGHT_COUNT = prefersReducedMotion ? 70 : 160;
    const COUNT = Math.max(DARK_COUNT, LIGHT_COUNT);
    const ATTRACT_RADIUS = 160;
    const ATTRACT_RADIUS_SQ = ATTRACT_RADIUS * ATTRACT_RADIUS;
    const SPRITE_SIZE = 28;
    // Light-mode swarm tunables — kept here so they're easy to tweak.
    // Weak spring + moderate damping = the swarm GRADUALLY follows the
    // cursor on a fast flick rather than snapping to it. There's still a
    // little settle-bounce because friction < 1, but it's gentle. The
    // flow field below adds the liquid drift on top.
    const LIGHT_SPRING_K = 0.010;     // weak pull — swarm flows toward cursor
    const LIGHT_FRICTION = 0.975;     // inertia → smooth glide
    const LIGHT_FADE_RATE = 0.07;     // activity fade per frame at 60fps
    // Velocity clamp — each particle's actual cap is LIGHT_MAX_SPEED *
    // p.mobility, so some particles drag and others lead. Together with
    // the per-particle springK they create a SPREAD during a cursor
    // traverse: fast cells arrive first, slow cells trail behind,
    // producing the "liquid breaking up and reforming" look instead of a
    // rigid block sliding uniformly.
    const LIGHT_MAX_SPEED = 2.8; // per-particle cap = LIGHT_MAX_SPEED * p.mobility
    // "Breath" — global pulsation of the orbital radius (the swarm as a
    // whole inhales/exhales) AND a per-particle pulse (each dot fades in
    // and out at its own rhythm, like cells alive). Period ≈ 3.6s for the
    // global breath, 4.5s for the per-particle breath — slightly off so
    // the two rhythms interfere and the cloud never repeats exactly.
    const LIGHT_BREATH_PERIOD_MS = 3600;
    const LIGHT_BREATH_AMPLITUDE = 0.10;   // gentle overall radius pulse (subtle vibration height)
    const LIGHT_PARTICLE_BREATH_MS = 4500;
    // Liquid flow field — each (x,y,t) maps to a velocity vector via a sum
    // of sinusoids. Particles are pushed along the field on top of the
    // spring-to-target force; in different areas of the hero the field
    // pulls in different directions, so the swarm reads as currents in a
    // pond rather than synchronized cells. The field varies slowly with
    // time so the currents themselves drift.
    const LIGHT_FLOW_STRENGTH = 0.55;      // force magnitude per dt
    const LIGHT_FLOW_SCALE = 0.0045;       // spatial frequency (smaller = bigger eddies)
    const LIGHT_FLOW_TIME_RATE = 0.00045;  // how fast the field itself drifts
    // Per-particle weave — sideways oscillation PERPENDICULAR to the
    // particle's current velocity. Each particle has its own phase, so
    // during a cursor traverse every cell takes a unique snake-like path
    // instead of moving in a straight line with the rest. Amplitude is
    // scaled by current speed so resting particles don't wiggle.
    const LIGHT_WEAVE_STRENGTH = 0.40;
    const LIGHT_WEAVE_RATE = 0.0008;       // rad/ms (period ≈ 7.9s — long, graceful curves)
    // Hue cycle period — slow rainbow shift so the swarm drifts purple →
    // blue → cyan → green → … over time. Lightness is determined by each
    // particle's position (dark in upper-left, light in lower-right) so
    // the cloud reads as a diagonal gradient that changes color together.
    const LIGHT_HUE_CYCLE_MS = 9000;
    // Smoothed "activity" value: 1 while the cursor is on the canvas, 0 when
    // off. All light-mode alphas multiply by this so the swarm fades in/out
    // gracefully on mouse enter/leave.
    let lightActivity = 0;

    const mouse = { x: -9999, y: -9999, active: false };

    const readVar = (name: string, fallback: string) => {
      const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      return v || fallback;
    };

    let sprites: HTMLCanvasElement[] = [];
    const buildSprites = () => {
      sprites = [
        makeSprite(readVar('--primary', '220 85% 58%'), SPRITE_SIZE),
        makeSprite(readVar('--accent', '265 85% 65%'), SPRITE_SIZE),
        makeSprite(readVar('--ring', '220 85% 58%'), SPRITE_SIZE),
      ];
    };
    buildSprites();

    // Only rebuild sprites when the theme actually changes. Debounced.
    let themeTimer: ReturnType<typeof setTimeout> | undefined;
    const scheduleRebuild = () => {
      if (themeTimer) clearTimeout(themeTimer);
      themeTimer = setTimeout(buildSprites, 60);
    };
    const obs = new MutationObserver(scheduleRebuild);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style', 'data-theme'],
    });

    const resize = () => {
      // Cap DPR at 1.5 — particles look fine and this halves pixel work on retina.
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const init = () => {
      particles.length = 0;
      // Light-mode swarm geometry — an evenly-spaced "donut" around the
      // cursor. Uses PHYLLOTAXIS (sunflower) distribution:
      //   • Angular phase advances by the golden angle (≈ 137.508°) per
      //     particle — the most irrational angle, so particles never
      //     align in radial spokes and the angular spacing is maximally
      //     even.
      //   • Radii follow sqrt(i / N) so the area density (and therefore
      //     the visual spacing between neighbours) is constant from
      //     inner to outer edge.
      //   • A tiny random jitter is added to both so the pattern doesn't
      //     read as a rigid grid — slightly organic.
      const LIGHT_MIN_ORBIT_R = 100;
      const LIGHT_MAX_ORBIT_R = Math.max(220, Math.min(width, height) * 0.42);
      const minSq = LIGHT_MIN_ORBIT_R * LIGHT_MIN_ORBIT_R;
      const maxSq = LIGHT_MAX_ORBIT_R * LIGHT_MAX_ORBIT_R;
      const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5)); // ≈ 2.39996 rad
      for (let i = 0; i < COUNT; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.15,
          vy: (Math.random() - 0.5) * 0.15,
          r: Math.random() * 1.6 + 0.8,
          baseAlpha: Math.random() * 0.45 + 0.4,
          phase: Math.random() * Math.PI * 2,
          // Even distribution across the 3 colors — unchanged from the
          // original dark-mode behavior. Both modes use this same index.
          colorIdx: (i % 3) as 0 | 1 | 2,
          rotation: Math.random() * Math.PI * 2,
          // Phyllotaxis (sunflower) distribution.
          //   • orbitR uses sqrt(i/N) so the area density is constant
          //     across the donut — no bunching.
          //   • orbitPhase advances by the golden angle each particle, the
          //     most irrational angle, which produces the most even
          //     angular distribution possible across the swarm.
          //   • Tiny random jitter on both keeps the pattern from looking
          //     mechanically rigid — slightly organic.
          orbitR:
            Math.sqrt(
              minSq + ((i + 0.5) / COUNT) * (maxSq - minSq),
            ) + (Math.random() - 0.5) * 6,
          orbitPhase:
            i * GOLDEN_ANGLE + (Math.random() - 0.5) * 0.05,
          orbitSpeed: (Math.random() - 0.5) * 0.0015,  // unused with no rotation; reserved
          // 0.5 .. 1.5 — sluggish cells drag, nimble cells lead during a
          // cursor traverse. Multiplier on both spring K and max speed.
          mobility: 0.5 + Math.random() * 1.0,
        });
      }
    };

    resize();
    init();

    const handleResize = () => {
      resize();
      init();
    };
    window.addEventListener('resize', handleResize);

    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      mouse.x = x;
      mouse.y = y;
      mouse.active = x >= 0 && x <= width && y >= 0 && y <= height;
    };
    const onMouseLeave = () => {
      mouse.active = false;
    };
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('mouseout', onMouseLeave);

    // Pause when tab/section is not visible to save battery.
    let running = true;
    const io = new IntersectionObserver(
      (entries) => {
        running = entries[0]?.isIntersecting ?? true;
        if (running) raf = requestAnimationFrame(loop);
      },
      { threshold: 0 },
    );
    io.observe(canvas);

    let raf = 0;
    let lastTime = performance.now();

    const loop = (now: number = performance.now()) => {
      if (!running) return;

      // Delta-time clamped so a long pause doesn't fling particles across the screen.
      const dt = Math.min((now - lastTime) / 16.667, 2.5);
      lastTime = now;

      ctx.clearRect(0, 0, width, height);

      // Composite mode: additive glows look premium on dark backgrounds and let
      // overlapping particles bloom. On a LIGHT background, additive blending
      // washes the colors to white and the particles disappear — use regular
      // alpha blending there so the dashes render as visible colored marks.
      const isLightTheme = themeRef.current === 'light';
      ctx.globalCompositeOperation = isLightTheme ? 'source-over' : 'lighter';

      // Soft cursor halo — dark theme only. The light theme uses the swarm
      // density around the cursor for cursor presence; no glow halo.
      if (mouse.active && !isLightTheme) {
        const sprite = sprites[0];
        ctx.globalAlpha = 0.35;
        ctx.drawImage(sprite, mouse.x - 90, mouse.y - 90, 180, 180);
        ctx.globalAlpha = 1;
      }

      // Light theme: smooth swarm-wide alpha multiplier. lightActivity moves
      // toward 1 while the cursor is on the canvas and toward 0 when it isn't,
      // so the swarm fades in/out gracefully on enter/leave.
      if (isLightTheme) {
        const target = mouse.active ? 1 : 0;
        lightActivity += (target - lightActivity) * Math.min(LIGHT_FADE_RATE * dt, 0.5);
      }

      // ────────────────────────────────────────────────────────────────────
      // LIGHT MODE — "AI Life" swarm:
      //   • Wide ring of dots around the cursor with an empty donut hole at
      //     the centre (the cursor itself never has dots immediately on top
      //     of it).
      //   • GLOBAL breath: orbital radius pulses (inhale toward cursor,
      //     exhale outward) on a slow ~3.6s cycle.
      //   • PER-PARTICLE breath: each dot fades in / out at its own ~4.5s
      //     rhythm so the cloud feels alive (different cells, different
      //     pulses).
      //   • COLOUR: vertical gradient (blue top → orange bottom) with a
      //     slow ~9s time-based hue rotation so the whole gradient drifts
      //     through the rainbow.
      // ────────────────────────────────────────────────────────────────────
      if (isLightTheme) {
        // Skip rendering entirely when the swarm is invisible AND the
        // cursor is off-canvas — keeps the idle hero free.
        if (lightActivity < 0.005 && !mouse.active) {
          raf = requestAnimationFrame(loop);
          return;
        }
        // Global radius breath: one number per frame, applied to every
        // particle's orbital radius. With amplitude 0.45 it ranges
        // 0.55 .. 1.45 — a dramatic in/out lung.
        const breath =
          1 + LIGHT_BREATH_AMPLITUDE *
            Math.sin((now * Math.PI * 2) / LIGHT_BREATH_PERIOD_MS);

        // Time-based hue rotation: at t=0 the top of the screen is blue
        // and the bottom is orange; over LIGHT_HUE_CYCLE_MS the whole
        // gradient drifts through the colour wheel.
        const hueOffset = (now * 360) / LIGHT_HUE_CYCLE_MS;

        for (let i = 0; i < LIGHT_COUNT; i++) {
          const p = particles[i];

          if (mouse.active) {
            // Spring toward (cursor + this particle's static orbital
            // offset, scaled by the shared breath). Each particle's
            // mobility scales the spring K, so different cells respond
            // at different rates — the swarm breaks up during a fast
            // traverse instead of moving as a rigid block.
            const r = p.orbitR * breath;
            const tx = mouse.x + Math.cos(p.orbitPhase) * r;
            const ty = mouse.y + Math.sin(p.orbitPhase) * r;
            const springK = LIGHT_SPRING_K * p.mobility;
            p.vx += (tx - p.x) * springK * dt;
            p.vy += (ty - p.y) * springK * dt;
          }

          // Liquid flow field — a position+time-dependent velocity push
          // overlaid on the spring force. The two unequal sinusoid rates
          // on X and Y mean the field forms non-repeating eddies; the
          // slow time drift makes the currents themselves move. Net
          // effect: the swarm reads as drifting in a pond, not snapping
          // to grid points.
          const flowT = now * LIGHT_FLOW_TIME_RATE;
          const flowAngle =
            Math.sin(p.x * LIGHT_FLOW_SCALE + flowT) +
            Math.cos(p.y * LIGHT_FLOW_SCALE + flowT * 1.3) +
            Math.sin((p.x + p.y) * LIGHT_FLOW_SCALE * 0.7 + flowT * 0.6);
          p.vx += Math.cos(flowAngle) * LIGHT_FLOW_STRENGTH * dt;
          p.vy += Math.sin(flowAngle) * LIGHT_FLOW_STRENGTH * dt;

          // Per-particle perpendicular weave — sideways oscillation
          // relative to current velocity. Each particle has a unique
          // phase so every dot snakes along its own wavy path during
          // transit. Amplitude scales with current speed so resting
          // particles don't wiggle, but moving ones weave organically.
          const speedSqPre = p.vx * p.vx + p.vy * p.vy;
          if (speedSqPre > 0.04) {
            const speed = Math.sqrt(speedSqPre);
            const invSpeed = 1 / speed;
            const perpX = -p.vy * invSpeed;
            const perpY = p.vx * invSpeed;
            const weave =
              Math.sin(now * LIGHT_WEAVE_RATE + p.phase * 1.7) *
              speed * LIGHT_WEAVE_STRENGTH;
            p.vx += perpX * weave * dt;
            p.vy += perpY * weave * dt;
          }

          p.vx *= LIGHT_FRICTION;
          p.vy *= LIGHT_FRICTION;

          // Velocity clamp — each particle's max speed scales with its
          // mobility, so sluggish cells trail and nimble cells lead. The
          // visual result: when the cursor moves a long distance, the
          // swarm spreads into a comet-like trail before reconverging
          // around the new cursor position. Combined with the flow field
          // perpendicular drift, the transit looks alive rather than
          // mechanically uniform.
          const pMaxSpeed = LIGHT_MAX_SPEED * p.mobility;
          const speedSq = p.vx * p.vx + p.vy * p.vy;
          if (speedSq > pMaxSpeed * pMaxSpeed) {
            const scale = pMaxSpeed / Math.sqrt(speedSq);
            p.vx *= scale;
            p.vy *= scale;
          }

          p.x += p.vx * dt;
          p.y += p.vy * dt;

          // Per-particle alpha breath — each dot fades in/out at its own
          // rhythm. The static `p.phase` gives every particle a unique
          // offset so the cloud is never fully bright or fully dark; some
          // cells inhale while others exhale.
          const pBreathPhase =
            (now * Math.PI * 2) / LIGHT_PARTICLE_BREATH_MS + p.phase;
          const pBreath = 0.5 + 0.5 * Math.sin(pBreathPhase); // 0..1
          const alpha =
            (0.55 + p.baseAlpha * 0.4) * lightActivity * pBreath;
          if (alpha < 0.02) continue;

          // Colour: vertical position determines the base hue (blue at
          // top, orange at bottom). The slow time hueOffset shifts the
          // whole gradient through the rainbow.
          const yRatio = Math.max(0, Math.min(1, p.y / height));
          const hue = (220 - yRatio * 190 + hueOffset + 360) % 360;
          const lightness = 38 + (1 - yRatio) * 8; // slightly darker at top
          ctx.fillStyle = `hsl(${hue}, 72%, ${lightness}%)`;

          // Standard-size dot with a subtle size breath so each cell
          // also visibly grows/shrinks (smaller amplitude than alpha so
          // it reads as a gentle pulse, not blinking).
          const dotR = p.r * 1.6 * (0.7 + 0.3 * pBreath);
          ctx.globalAlpha = alpha;
          ctx.beginPath();
          ctx.arc(p.x, p.y, dotR, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
        raf = requestAnimationFrame(loop);
        return;
      }

      // ────────────────────────────────────────────────────────────────────
      // DARK MODE — existing physics + glow sprites, unchanged.
      // ────────────────────────────────────────────────────────────────────
      for (let i = 0; i < DARK_COUNT; i++) {
        const p = particles[i];

        if (mouse.active) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist2 = dx * dx + dy * dy;
          if (dist2 < ATTRACT_RADIUS_SQ && dist2 > 1) {
            const falloff = 1 - dist2 / ATTRACT_RADIUS_SQ;
            const force = falloff * 0.05 * dt;
            const invD = 1 / Math.sqrt(dist2);
            p.vx += dx * invD * force;
            p.vy += dy * invD * force;
          }
        }

        // friction + gentle jitter
        p.vx *= 0.965;
        p.vy *= 0.965;
        p.vx += (Math.random() - 0.5) * 0.008 * dt;
        p.vy += (Math.random() - 0.5) * 0.008 * dt;

        p.x += p.vx * dt;
        p.y += p.vy * dt;

        if (p.x < -20) p.x = width + 20;
        else if (p.x > width + 20) p.x = -20;
        if (p.y < -20) p.y = height + 20;
        else if (p.y > height + 20) p.y = -20;

        const twinkle = 0.6 + 0.4 * Math.sin(now * 0.002 + p.phase);
        const alpha = p.baseAlpha * twinkle;
        const size = p.r * 8;
        ctx.globalAlpha = alpha;
        ctx.drawImage(sprites[p.colorIdx], p.x - size / 2, p.y - size / 2, size, size);
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      // (Even when we ran the loop with 'lighter', we restore 'source-over'
      // before yielding so any sibling canvas work isn't surprised.)

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      if (themeTimer) clearTimeout(themeTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseout', onMouseLeave);
      obs.disconnect();
      io.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ willChange: 'transform' }}
    />
  );
}
